import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// @ts-ignore - Remotion type compatibility
import { Player, PlayerRef } from '@remotion/player';
import Sidebar from '../../components/sidebar/Sidebar';
import BetaAlert from '../../components/common/BetaAlert';
import VideoGenerationTab from '../../components/video-generation/VideoGenerationTab';
import VideoExportButton from '../../components/video-generation/VideoExportButton';
import EasyVideoTab from '../../components/easy-video/EasyVideoTab';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import type { RootState } from '../../redux/store';
import { useRemotionExport } from '../../hooks/useRemotionExport';
import { ResponsiveAppShell, TaskyTabs, TaskyTopBar, type TaskyTabItem, TaskyPageContainer } from '../../components/tasky';
import {
  TikTokStyle,
  ShopIntroStyle,
  UgcStyle,
} from '../../remotion';
import { Video, RefreshCw, Smartphone, Monitor, Square, Sparkles, Film, Wand2 } from 'lucide-react';

type TabType = 'ai-generate' | 'template' | 'easy';
type TemplateType = 'tiktok' | 'shop-intro' | 'ugc';

const VIDEO_TAB_ITEMS: TaskyTabItem<TabType>[] = [
  {
    value: 'ai-generate',
    label: 'AI動画生成',
    icon: <Sparkles className="w-4 h-4" />,
  },
  {
    value: 'template',
    label: (
      <span className="inline-flex items-center gap-2">
        テンプレート動画
        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
          β
        </span>
      </span>
    ),
    icon: <Film className="w-4 h-4" />,
  },
];

/** 組織フラグ enable_easy_video が ON のときだけ出すタブ */
const EASY_VIDEO_TAB_ITEM: TaskyTabItem<TabType> = {
  value: 'easy',
  label: 'かんたん作成',
  icon: <Wand2 className="w-4 h-4" />,
};

const VideoAdsPage: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as {
    refImageS3Key?: string;
    refImageUrl?: string;
    refPrompt?: string;
    preferredStyle?: string;
    imageAspectRatio?: '1:1' | '16:9' | '9:16';
  } | null;
  const refImageS3Key = locationState?.refImageS3Key;
  const refImageUrl = locationState?.refImageUrl;
  const refPrompt = locationState?.refPrompt;
  const preferredStyle = locationState?.preferredStyle;
  const imageAspectRatio = locationState?.imageAspectRatio;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const organization = useSelector(selectOrganization);
  const easyVideoEnabled = !!organization?.enable_easy_video;
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id ?? null);
  // ?tab=easy&project=<uuid> で「かんたん作成」の状態を復元する（他のタブは URL を使わない）
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    searchParams.get('tab') === 'easy' ? 'easy' : 'ai-generate'
  );
  // フラグが OFF（または組織の読み込み前）は、かんたん作成を選んでいても AI動画生成を出す
  const currentTab: TabType = activeTab === 'easy' && !easyVideoEnabled ? 'ai-generate' : activeTab;
  const easyProjectId = searchParams.get('project');
  const tabItems = useMemo(
    () => (easyVideoEnabled ? [...VIDEO_TAB_ITEMS, EASY_VIDEO_TAB_ITEM] : VIDEO_TAB_ITEMS),
    [easyVideoEnabled]
  );

  // URL を書き換えても、画像生成から渡された location.state（参照画像など）は保持する
  const updateEasyParams = useCallback(
    (next: { tab: boolean; project?: string | null }, replace: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (next.tab) params.set('tab', 'easy');
      else params.delete('tab');
      if (next.tab && next.project) params.set('project', next.project);
      else params.delete('project');
      setSearchParams(params, { replace, state: location.state });
    },
    [searchParams, setSearchParams, location.state]
  );

  const handleTabChange = useCallback(
    (tab: TabType) => {
      setActiveTab(tab);
      if (tab === 'easy') updateEasyParams({ tab: true, project: easyProjectId }, true);
      else if (searchParams.get('tab') === 'easy') updateEasyParams({ tab: false }, true);
    },
    [updateEasyParams, easyProjectId, searchParams]
  );

  const handleEasyProjectChange = useCallback(
    (projectId: string | null) => updateEasyParams({ tab: true, project: projectId }, false),
    [updateEasyParams]
  );
  const playerRef = useRef<PlayerRef>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);

  // Template selection
  const [templateType, setTemplateType] = useState<TemplateType>('tiktok');
  const [aspectRatio, setAspectRatio] = useState<'1:1' | '16:9' | '9:16'>('9:16');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [playerKey, setPlayerKey] = useState(0);

  // TikTok style fields
  const [headline, setHeadline] = useState('');
  const [bodyLines, setBodyLines] = useState('');
  const [accentColor, setAccentColor] = useState('#ec4899');

  // Shop intro style fields
  const [shopName, setShopName] = useState('');
  const [tagline, setTagline] = useState('');
  const [features, setFeatures] = useState('');

  // UGC style fields
  const [username, setUsername] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [productName, setProductName] = useState('');
  const [rating, setRating] = useState(5);

  // Common
  const [ctaText, setCtaText] = useState('');

  const handleUpdatePreview = useCallback(() => {
    setPlayerKey(prev => prev + 1);
  }, []);

  // Template definitions
  const templates = [
    {
      id: 'tiktok' as TemplateType,
      label: 'TikTok / Reels風',
      description: 'テンポの良い短尺動画。SNS広告に最適',
      icon: '📱',
      defaultAspect: '9:16' as const,
    },
    {
      id: 'shop-intro' as TemplateType,
      label: '店舗・ブランド紹介',
      description: '上品でエレガントなブランド訴求',
      icon: '🏪',
      defaultAspect: '16:9' as const,
    },
    {
      id: 'ugc' as TemplateType,
      label: 'UGC / レビュー風',
      description: 'ユーザー体験談・口コミ風の自然な訴求',
      icon: '💬',
      defaultAspect: '9:16' as const,
    },
  ];

  const handleTemplateSelect = (id: TemplateType) => {
    setTemplateType(id);
    const tpl = templates.find(t => t.id === id);
    if (tpl) {
      setAspectRatio(tpl.defaultAspect);
    }
  };

  // Render the Remotion composition based on template type
  const renderComposition = () => {
    switch (templateType) {
      case 'tiktok':
        return (
          <TikTokStyle
            headline={headline || 'あなたの広告が変わる'}
            bodyLines={
              bodyLines
                ? bodyLines.split('\n').filter(l => l.trim())
                : ['AIが自動で動画を生成', '数分で完成する広告', '効果の高いクリエイティブを']
            }
            ctaText={ctaText || '今すぐ試す'}
            primaryColor={primaryColor}
            accentColor={accentColor}
            aspectRatio={aspectRatio}
          />
        );
      case 'shop-intro':
        return (
          <ShopIntroStyle
            shopName={shopName || 'Sample Brand'}
            tagline={tagline || 'こだわりの品質をあなたへ'}
            features={
              features
                ? features.split('\n').filter(l => l.trim())
                : ['厳選された素材', '職人の手仕事', '全国送料無料']
            }
            ctaText={ctaText || '詳しく見る'}
            primaryColor={primaryColor}
            aspectRatio={aspectRatio}
          />
        );
      case 'ugc':
        return (
          <UgcStyle
            username={username || 'user_123'}
            reviewText={reviewText || 'これは本当に素晴らしい商品です。使い始めてから生活が変わりました！'}
            productName={productName || 'サンプル商品'}
            rating={rating}
            ctaText={ctaText || '私も試してみる'}
            primaryColor={primaryColor}
            aspectRatio={aspectRatio}
          />
        );
      default:
        return null;
    }
  };

  // Duration based on template
  const getDuration = () => {
    switch (templateType) {
      case 'tiktok':
        return 240; // 8 seconds
      case 'shop-intro':
        return 300; // 10 seconds
      case 'ugc':
        return 360; // 12 seconds
      default:
        return 270;
    }
  };

  const getDimensions = () => {
    return aspectRatio === '16:9'
      ? { width: 1920, height: 1080 }
      : aspectRatio === '1:1'
      ? { width: 1080, height: 1080 }
      : { width: 1080, height: 1920 };
  };

  const dimensions = getDimensions();

  const { startExport, progress, isExporting, error: exportError } = useRemotionExport({
    playerRef,
    containerRef: playerContainerRef,
    durationInFrames: getDuration(),
    fps: 30,
    width: dimensions.width,
    height: dimensions.height,
  });

  const renderTemplateTab = () => (
    <TaskyPageContainer>
      <BetaAlert
        featureName="テンプレート動画"
        customMessage="テンプレートを選択し、テキストを入力するとプレビューが表示されます。「動画をダウンロード」ボタンでWebM形式の動画をエクスポートできます。"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Left: Input Form */}
        <div className="space-y-6">
          {/* Template Selection */}
          <div className="bg-white rounded-lg border border-warm-border p-6">
            <h3 className="text-base font-semibold mb-4">テンプレート選択</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => handleTemplateSelect(tpl.id)}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    templateType === tpl.id
                      ? 'border-purple bg-purple/10'
                      : 'border-warm-border hover:border-purple/50 hover:bg-purple/5'
                  }`}
                >
                  <span className="text-3xl mb-2">{tpl.icon}</span>
                  <span className="text-sm font-medium text-gray-800">{tpl.label}</span>
                  <span className="text-xs text-gray-500 mt-1 text-center">{tpl.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template-specific inputs */}
          {templateType === 'tiktok' && (
            <div className="bg-white rounded-lg border border-warm-border p-6 space-y-4">
              <h3 className="text-base font-semibold">TikTok / Reels風 設定</h3>
              <div>
                <label className="block text-sm font-medium mb-1">ヘッドライン</label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="例: あなたの広告が変わる"
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  本文（改行区切り、最大4行）
                </label>
                <textarea
                  value={bodyLines}
                  onChange={(e) => setBodyLines(e.target.value)}
                  placeholder={"AIが自動で動画を生成\n数分で完成する広告\n効果の高いクリエイティブを"}
                  rows={4}
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">アクセントカラー</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="h-10 w-16 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="flex-1 border border-warm-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {templateType === 'shop-intro' && (
            <div className="bg-white rounded-lg border border-warm-border p-6 space-y-4">
              <h3 className="text-base font-semibold">店舗・ブランド紹介 設定</h3>
              <div>
                <label className="block text-sm font-medium mb-1">店舗 / ブランド名</label>
                <input
                  type="text"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  placeholder="例: Sample Brand"
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">タグライン</label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="例: こだわりの品質をあなたへ"
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  特徴（改行区切り、最大3つ）
                </label>
                <textarea
                  value={features}
                  onChange={(e) => setFeatures(e.target.value)}
                  placeholder={"厳選された素材\n職人の手仕事\n全国送料無料"}
                  rows={3}
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          {templateType === 'ugc' && (
            <div className="bg-white rounded-lg border border-warm-border p-6 space-y-4">
              <h3 className="text-base font-semibold">UGC / レビュー風 設定</h3>
              <div>
                <label className="block text-sm font-medium mb-1">ユーザー名</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="例: user_123"
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">レビューテキスト</label>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="例: これは本当に素晴らしい商品です。使い始めてから生活が変わりました！"
                  rows={3}
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">商品名</label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="例: プレミアムスキンケアセット"
                  className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">評価</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className={`text-2xl transition-colors ${
                        star <= rating ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                    >
                      {'\u2B50'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Common settings */}
          <div className="bg-white rounded-lg border border-warm-border p-6 space-y-4">
            <h3 className="text-base font-semibold">共通設定</h3>

            <div>
              <label className="block text-sm font-medium mb-1">CTAテキスト</label>
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                placeholder="例: 今すぐ試す"
                className="w-full border border-warm-border rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">テーマカラー</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-10 w-16 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 border border-warm-border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">アスペクト比</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setAspectRatio('1:1')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    aspectRatio === '1:1'
                      ? 'bg-purple text-white border-purple'
                      : 'bg-white text-gray-700 border-warm-border hover:bg-gray-50'
                  }`}
                >
                  <Square className="w-4 h-4" />
                  1:1（横1080×縦1080px）
                </button>
                <button
                  onClick={() => setAspectRatio('9:16')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    aspectRatio === '9:16'
                      ? 'bg-purple text-white border-purple'
                      : 'bg-white text-gray-700 border-warm-border hover:bg-gray-50'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  9:16（横1080×縦1920px）
                </button>
                <button
                  onClick={() => setAspectRatio('16:9')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    aspectRatio === '16:9'
                      ? 'bg-purple text-white border-purple'
                      : 'bg-white text-gray-700 border-warm-border hover:bg-gray-50'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  16:9（横1920×縦1080px）
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleUpdatePreview}
              className="w-full bg-purple hover:bg-purple/90 text-white font-medium py-3 px-4 rounded-tasky-sm transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              プレビュー更新
            </button>
            <VideoExportButton
              onExport={startExport}
              progress={progress}
              isExporting={isExporting}
              error={exportError}
            />
          </div>
        </div>

        {/* Right: Preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <div className="bg-white rounded-lg border border-warm-border p-6">
            <h3 className="text-base font-semibold mb-4">プレビュー</h3>
            <div
              ref={playerContainerRef}
              className="bg-gray-900 rounded-lg overflow-hidden"
              style={{
                aspectRatio: aspectRatio === '16:9' ? '16 / 9' : aspectRatio === '1:1' ? '1 / 1' : '9 / 16',
                maxWidth: aspectRatio === '16:9' ? '100%' : aspectRatio === '1:1' ? '480px' : '360px',
                margin: '0 auto',
              }}
            >
              <Player
                ref={playerRef}
                key={playerKey}
                component={renderComposition}
                durationInFrames={getDuration()}
                fps={30}
                compositionWidth={dimensions.width}
                compositionHeight={dimensions.height}
                style={{
                  width: '100%',
                  height: '100%',
                }}
                controls
                loop
              />
            </div>
            <div className="mt-3 text-center">
              <span className="text-xs text-gray-500">
                {Math.round(getDuration() / 30)}秒 / {aspectRatio} / 30fps
              </span>
            </div>
          </div>
        </div>
      </div>
    </TaskyPageContainer>
  );

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="動画広告"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="動画広告"
          icon={<Video className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Tab Navigation */}
        <div data-tutorial="ads-video-tabs" className="shrink-0 overflow-x-auto bg-white px-4 pt-3 sm:px-6">
          <TaskyTabs<TabType>
            items={tabItems}
            value={currentTab}
            onChange={handleTabChange}
            aria-label="動画広告"
            className="w-full min-w-max"
          />
        </div>

        {/* Tab Content */}
        <div data-tutorial="ads-video-content" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {currentTab === 'easy' ? (
            <EasyVideoTab
              projectId={easyProjectId}
              onProjectIdChange={handleEasyProjectChange}
              currentUserId={currentUserId}
            />
          ) : currentTab === 'ai-generate' ? (
            <VideoGenerationTab
              refImageS3Key={refImageS3Key}
              refImageUrl={refImageUrl}
              refPrompt={refPrompt}
              preferredStyle={preferredStyle}
              imageAspectRatio={imageAspectRatio}
              generationSource="video_ads"
            />
          ) : (
            renderTemplateTab()
          )}
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default VideoAdsPage;
